import {
  Button,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditIcon } from '../utils/icons';
import { getDisplayLocale } from '../utils/i18n';

interface QuestionModuleProps {
  module: {
    order: number;
    content: any;
  };
  savedAnswer?: string;
  submissionDate?: Date | string;
  onSave: (answer: string) => void;
  getLocalizedText: (text: any, fallback?: string) => string;
}

const QuestionModule: React.FC<QuestionModuleProps> = ({
  module,
  savedAnswer,
  submissionDate,
  onSave,
  getLocalizedText
}) => {
  const { t, i18n } = useTranslation();
  const [answer, setAnswer] = useState(savedAnswer || '');
  const [isEditing, setIsEditing] = useState(!savedAnswer || savedAnswer.trim() === '');

  // Update local state when savedAnswer prop changes
  useEffect(() => {
    if (savedAnswer !== undefined) {
      setAnswer(savedAnswer);
      setIsEditing(savedAnswer.trim() === '');
    }
  }, [savedAnswer]);

  const handleSave = (e?: React.MouseEvent) => {
    console.log('💾 QuestionModule handleSave called');
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (answer.trim()) {
      console.log('💾 Calling onSave with answer:', answer);
      onSave(answer);
      setIsEditing(false);
    } else {
      console.log('💾 Answer is empty, not saving');
    }
  };

  const handleEdit = () => {
    // Just switch to edit mode, don't call API
    setIsEditing(true);
    // Keep the saved answer in the textarea so user can edit it
    setAnswer(savedAnswer || '');
  };

  const isSaved = !isEditing && !!savedAnswer && savedAnswer.trim() !== '';

  // Titre du module (multilingue, admin) → placeholder du textarea ; sinon libellé traduit « Répondre »
  const moduleTitle = (getLocalizedText(module.content?.title, '') || '').trim();
  const placeholder = moduleTitle || t('questionModule.defaultAnswerPlaceholder');

  // Format submission date
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';

    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    const day = d.getDate();
    const month = d.toLocaleDateString(locale, { month: 'short' });
    const year = d.getFullYear();
    const time = d.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });

    return t('questionModule.submittedOn', { day, month, year, time });
  };

  return (
    <div className="question-module" onClick={(e) => e.stopPropagation()}>
      {!isSaved ? (
        // État d'édition
        <div className="question-module__edit">
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder={placeholder}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            className="question-module__textarea"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="question-module__divider"></div>
          <div className="question-module__actions">
            <Button
              type="button"
              variant="contained"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('💾 Button clicked, calling handleSave');
                handleSave(e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="question-module__save-button"
            >
              {t('questionModule.saveButton')}
            </Button>
          </div>
        </div>
      ) : (
        // État de visualisation
        <div className="question-module__view">
          <div className="question-module__answer">
            <p>{savedAnswer}</p>
          </div>
          <div className="question-module__footer">
            <p className="question-module__date">{formatDate(submissionDate)}</p>
            <Button
              type="button"
              variant="contained"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEdit();
              }}
              className="question-module__edit-button"
              startIcon={<EditIcon size={16} />}
            >
              {t('questionModule.editButton')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionModule;
